import { useState } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import uniqid from 'uniqid';
import classNames from 'classnames';

import Icons from 'assets/icons';

import Report from 'containers/user/reports/report-section/report-tile/report-tile';

import { Stack } from 'ui/stack';
import { Box } from 'ui/box';

import { Typography } from 'ui/typography';

import {
  getProductTypeDescription,
  type ProductTypeDictionary,
} from 'containers/user/reports/reports-utils';
import type { ReportTileData } from 'containers/user/reports/report-section/report-tile/report-tile';

const CONTENT_SX: SxProps<Theme> = {
  flexWrap: 'wrap',
  gap: 2,
  alignItems: 'stretch',
  overflow: 'hidden',
  transitionProperty: 'all',
  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
  transitionDuration: '300ms',
};

interface ReportSectionProps {
  productType: string;
  reports: ReportTileData[];
  dictionary: ProductTypeDictionary;
  isDefaultOpen: boolean;
}

const ReportSection = ({
  productType,
  reports,
  dictionary,
  isDefaultOpen,
}: ReportSectionProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(isDefaultOpen);

  const contentId = `report-section-${productType}`;

  const getIconForProductType = (
    productTypeArg: string
  ): React.JSX.Element | null => {
    const inconsStyles = 'text-navy-800 size-5';

    switch (productTypeArg) {
      case 'PROSPECT_VIEW':
        return (
          <Icons.ProspectViewFunnel
            customClass={inconsStyles}
            aria-hidden="true"
          />
        );
      case 'ADMISSIONS_VIEW':
        return <Icons.Learn customClass={inconsStyles} aria-hidden="true" />;
      case 'FINANCIAL_AID_VIEW':
        return <Icons.Finance customClass={inconsStyles} aria-hidden="true" />;
      case 'RETENTION_VIEW':
        return <Icons.People customClass={inconsStyles} aria-hidden="true" />;
      default:
        return null;
    }
  };

  const toggleSection = (): void => {
    setIsOpen(!isOpen);
  };

  return (
    <Box sx={{ my: 2.5 }}>
      <button
        className="flex items-center mb-4 transition-colors duration-200"
        onClick={toggleSection}
        aria-expanded={isOpen}
        aria-controls={contentId}
      >
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          {getIconForProductType(productType)}
          <Typography
            data-testid="report-section-heading"
            variant="h3"
            sx={{ ml: 1 }}
          >
            {getProductTypeDescription(productType, dictionary) || productType}
          </Typography>
        </Stack>
        {!!reports.length && (
          <Icons.Arrow
            className={classNames(
              'text-navy-800 ml-2 transform transition-all duration-300 ease-in-out',
              { 'rotate-90': isOpen, 'rotate-0': !isOpen }
            )}
            aria-hidden="true"
          />
        )}
      </button>

      <Stack
        id={contentId}
        direction="row"
        sx={[
          CONTENT_SX,
          isOpen && { maxHeight: '2000px', opacity: 1, mt: 2 },
          !isOpen && { maxHeight: 0, opacity: 0, mt: 0 },
        ]}
      >
        {reports.map((report) => (
          <Box key={uniqid()} sx={{ width: '328px', mb: 2.5 }}>
            <Report report={report} isOpen={isOpen} />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default ReportSection;
